import React, { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import Seo, { breadcrumbJsonLd, serviceJsonLd } from "../components/Seo";
import { getServiceBySlug, GOOGLE_FORM_FALLBACKS } from "../data/siteData";
import { ArrowUpRight, Check } from "lucide-react";

const Eyebrow = ({ children, light = false }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className={`h-px w-10 ${light ? "bg-[#D4AF37]" : "bg-[#C0C0C0]"}`} />
    <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
      {children}
    </span>
  </div>
);

const ListSection = ({ title, intro, items, outro, testId }) => (
  <div data-testid={testId}>
    <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F]">{title}</h2>
    {intro && <p className="mt-3 text-sm md:text-base leading-relaxed text-[#475569]">{intro}</p>}
    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm md:text-base text-[#0A192F]">
          <span className="w-1.5 h-1.5 bg-[#D4AF37] rotate-45 mt-2 flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
    {outro && <p className="mt-5 text-sm md:text-base leading-relaxed text-[#475569]">{outro}</p>}
  </div>
);

const HeroSection = ({ service }) => (
  <section className="relative bg-[#0A192F] text-white pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(55% 80% at 80% 15%, rgba(212,175,55,0.2) 0%, transparent 60%), radial-gradient(45% 60% at 10% 90%, rgba(30,58,138,0.5) 0%, transparent 60%)",
      }}
      aria-hidden="true"
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <Eyebrow light>{service.eyebrow}</Eyebrow>
      <h1
        data-testid="service-headline"
        className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.02] max-w-4xl"
      >
        {service.h1}
      </h1>
      {service.positioning.map((p) => (
        <p key={p.slice(0, 40)} className="mt-6 text-base md:text-lg text-[#C0C0C0] leading-relaxed max-w-2xl">
          {p}
        </p>
      ))}

      <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#94A3B8] font-semibold">Investment</div>
          <div data-testid="service-investment" className="mt-1 font-display text-xl text-[#D4AF37] font-semibold">
            {service.investment}
          </div>
        </div>
        {service.timeline && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#94A3B8] font-semibold">Timeline</div>
            <div className="mt-1 font-display text-xl text-white font-semibold">{service.timeline}</div>
          </div>
        )}
      </div>

      <div className="mt-9 flex flex-col sm:flex-row gap-4">
        <a
          href={service.waLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="service-hero-cta"
          className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-7 py-3.5 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
        >
          {service.waLabel} →
        </a>
        {service.secondaryCtaTo && (
          <Link
            to={service.secondaryCtaTo}
            data-testid="service-hero-secondary"
            className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-[#C0C0C0]/40 px-6 py-3 text-sm font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
          >
            {service.secondaryCtaLabel}
          </Link>
        )}
      </div>
    </div>
  </section>
);

const MethodologySection = ({ methodology }) => (
  <section data-testid="service-methodology" className="bg-white py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F] mb-10">
        The methodology
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {methodology.map((m, i) => (
          <div key={m.title} className="bg-[#F8F9FA] border border-[#C0C0C0]/40 p-6">
            <div className="font-display text-3xl text-[#D4AF37] font-semibold opacity-40">0{i + 1}</div>
            <h3 className="mt-3 font-display text-lg font-semibold text-[#0A192F]">{m.title}</h3>
            <p className="mt-2 text-sm text-[#475569] leading-relaxed">{m.blurb}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DeliverablesSection = ({ deliverables, callout }) => (
  <section data-testid="service-deliverables" className="bg-white py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className={`grid grid-cols-1 ${deliverables.length > 1 ? "lg:grid-cols-2" : ""} gap-10`}>
        {deliverables.map((group) => (
          <div key={group.title} className="border border-[#C0C0C0]/40 p-7 md:p-9">
            <h2 className="font-display text-xl md:text-2xl font-semibold text-[#0A192F]">{group.title}</h2>
            <ul className="mt-5 space-y-2.5">
              {group.items.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm md:text-base text-[#0A192F]">
                  <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {callout && (
        <div className="mt-8 bg-[#0A192F] text-white px-6 py-5 text-sm md:text-base border-l-4 border-[#D4AF37]">
          {callout}
        </div>
      )}
    </div>
  </section>
);

const PackagesSection = ({ title, packages }) => (
  <section data-testid="service-packages" className="bg-[#0A192F] text-white py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <h2 className="font-display text-2xl md:text-3xl font-semibold mb-10">{title}</h2>
      <div className={`grid grid-cols-1 md:grid-cols-2 ${packages.length > 2 ? "xl:grid-cols-4" : ""} gap-6`}>
        {packages.map((pkg) => (
          <div
            key={pkg.name}
            data-testid={`package-${pkg.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`relative bg-[#0F2542] border p-7 flex flex-col ${
              pkg.popular ? "border-[#D4AF37]" : "border-[#C0C0C0]/20"
            }`}
          >
            {pkg.popular && (
              <div className="absolute -top-3 left-6 bg-[#D4AF37] text-[#0A192F] text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1">
                Most Popular
              </div>
            )}
            <h3 className="font-display text-lg md:text-xl font-semibold">{pkg.name}</h3>
            <div className="mt-2 font-display text-2xl text-[#D4AF37] font-semibold">{pkg.price}</div>
            {pkg.blurb && <p className="mt-3 text-sm text-[#C0C0C0] leading-relaxed">{pkg.blurb}</p>}
            <ul className="mt-5 space-y-2 flex-1">
              {pkg.items.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-[#C0C0C0]">
                  <Check className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            {pkg.note && (
              <p className="mt-5 pt-4 border-t border-[#C0C0C0]/15 text-xs text-[#94A3B8] leading-relaxed">
                {pkg.note}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ProcessSection = ({ process }) => (
  <section data-testid="service-process" className="bg-white py-16 md:py-24">
    <div className="max-w-5xl mx-auto px-6 md:px-12">
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F] mb-8">Process</h2>
      <ol className="space-y-4">
        {process.map((step, i) => (
          <li key={step} className="flex items-start gap-4 border-b border-[#C0C0C0]/30 pb-4">
            <span className="font-display text-lg text-[#D4AF37] font-semibold w-8 flex-shrink-0">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-sm md:text-base text-[#0A192F]">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const ExclusionsSection = ({ service }) => (
  <section data-testid="service-exclusions" className="bg-[#F8F9FA] py-16 md:py-24">
    <div className={`max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 ${service.notIncluded ? "lg:grid-cols-2" : ""} gap-10`}>
      {service.notIncluded && (
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F]">Not included</h2>
          <ul className="mt-6 space-y-2.5">
            {service.notIncluded.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm md:text-base text-[#475569]">
                <span className="text-[#94A3B8] font-bold mt-0.5 flex-shrink-0">—</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {service.boundary && (
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F]">
            Important {service.boundary.length > 1 ? "boundaries" : "boundary"}
          </h2>
          <div className="mt-6 space-y-4">
            {service.boundary.map((b) => (
              <p key={b.slice(0, 40)} className="text-sm md:text-base text-[#475569] leading-relaxed border-l-2 border-[#D4AF37] pl-5">
                {b}
              </p>
            ))}
          </div>
          {service.philosophy && (
            <div className="mt-10 bg-[#0A192F] text-white p-7">
              <h3 className="font-display text-xl font-semibold text-[#D4AF37]">{service.philosophy.h2}</h3>
              <p className="mt-3 text-sm text-[#C0C0C0] leading-relaxed">{service.philosophy.blurb}</p>
            </div>
          )}
        </div>
      )}
    </div>
  </section>
);

const GallerySection = ({ gallery }) => (
  <section data-testid="service-gallery" className="bg-white py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F]">{gallery.title}</h2>
      {gallery.note && <p className="mt-3 text-sm text-[#475569]">{gallery.note}</p>}
      <div className={`mt-8 grid grid-cols-1 ${gallery.fullImage ? "sm:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-3"} gap-6`}>
        {gallery.images.map((img) => (
          <figure key={img.src} className="relative border border-[#C0C0C0]/40 overflow-hidden">
            {img.label && (
              <span className="absolute top-3 left-3 z-10 bg-[#0A192F]/90 text-[#D4AF37] text-[9px] uppercase tracking-[0.2em] font-bold px-2.5 py-1">
                {img.label}
              </span>
            )}
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className={gallery.fullImage ? "w-full h-auto block" : "w-full h-72 object-cover object-top"}
            />
          </figure>
        ))}
      </div>
      {!gallery.fullImage && (
        <div className="mt-8">
          <Link
            to="/case-studies"
            data-testid="service-gallery-cta"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A192F] border-b border-[#0A192F]/30 pb-1 hover:border-[#D4AF37] hover:text-[#1E3A8A] transition-colors"
          >
            View Full Case Studies <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  </section>
);

const FinalCtaSection = ({ service, fallbackForm }) => (
  <section data-testid="service-final-cta" className="relative bg-[#0A192F] text-white py-20 md:py-28 overflow-hidden">
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(60% 80% at 20% 30%, rgba(30,58,138,0.5) 0%, transparent 60%), radial-gradient(50% 60% at 90% 80%, rgba(212,175,55,0.3) 0%, transparent 60%)",
      }}
      aria-hidden="true"
    />
    <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold leading-[1.05]">
        {service.finalCta.h2}
      </h2>
      <p className="mt-6 text-sm md:text-base text-[#C0C0C0] leading-relaxed max-w-2xl mx-auto">
        {service.finalCta.blurb}
      </p>
      <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
        <a
          href={service.waLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="service-final-cta-btn"
          className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-8 py-4 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
        >
          {service.waLabel} <ArrowUpRight className="w-4 h-4" />
        </a>
        {fallbackForm && (
          <a
            href={fallbackForm}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="service-final-form-fallback"
            className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-[#C0C0C0]/40 px-7 py-3.5 text-sm font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
          >
            Prefer a form? Check My Fit
          </a>
        )}
      </div>
    </div>
  </section>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const jsonLd = useMemo(
    () =>
      service
        ? [
            serviceJsonLd(service),
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Services", path: "/services" },
              { name: service.shortName, path: `/services/${service.slug}` },
            ]),
          ]
        : null,
    [service]
  );

  if (!service) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`service-page-${service.slug}`} className="bg-white">
      <Seo
        title={service.seo.title}
        description={service.seo.description}
        path={`/services/${service.slug}`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <HeroSection service={service} />

      {service.problem && (
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <ListSection
              title={service.problem.title}
              intro={service.problem.intro}
              items={service.problem.items}
              outro={service.problem.outro}
              testId="service-problem"
            />
          </div>
        </section>
      )}

      {service.extraList && (
        <section className={`${service.problem ? "bg-[#F8F9FA]" : "bg-white"} py-16 md:py-24`}>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <ListSection
              title={service.extraList.title}
              intro={service.extraList.intro}
              items={service.extraList.items}
              outro={service.extraList.outro}
              testId="service-extra-list"
            />
          </div>
        </section>
      )}

      {service.methodology && <MethodologySection methodology={service.methodology} />}

      <section className="bg-[#F8F9FA] py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <ListSection title="Best suited for" items={service.bestFor} testId="service-best-for" />
        </div>
      </section>

      {service.deliverables && (
        <DeliverablesSection deliverables={service.deliverables} callout={service.callout} />
      )}

      {service.receiveGallery && <GallerySection gallery={service.receiveGallery} />}

      {service.packages && <PackagesSection title={service.packagesTitle} packages={service.packages} />}

      {service.process && <ProcessSection process={service.process} />}

      {service.licensing && (
        <section className="bg-[#F8F9FA] py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <ListSection
              title={service.licensing.title}
              intro={service.licensing.intro}
              items={service.licensing.items}
              outro={service.licensing.outro}
              testId="service-licensing"
            />
          </div>
        </section>
      )}

      {service.addons && (
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <ListSection title={service.addons.title} items={service.addons.items} testId="service-addons" />
          </div>
        </section>
      )}

      {(service.notIncluded || service.boundary) && <ExclusionsSection service={service} />}

      {service.gallery && <GallerySection gallery={service.gallery} />}

      <FinalCtaSection service={service} fallbackForm={GOOGLE_FORM_FALLBACKS[service.slug]} />

      <Footer />
    </div>
  );
}
